import type { ReactNode } from 'react';
import { AlertCircle, Inbox, Loader2, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api';
import { Button } from './Button';

export function LoadingState({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-ink-muted">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label ?? t('common.loading')}
    </div>
  );
}

export function EmptyState({ title, hint, icon, action }: {
  title: string; hint?: string; icon?: ReactNode; action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <span className="flex size-12 items-center justify-center rounded-full bg-canvas-soft text-ink-subtle">
        {icon ?? <Inbox className="size-5" aria-hidden />}
      </span>
      <p className="text-sm font-semibold text-ink">{title}</p>
      {hint && <p className="max-w-sm text-xs text-ink-muted">{hint}</p>}
      {action}
    </div>
  );
}

/**
 * Renders the backend's message verbatim - it arrives already translated,
 * with a request id the user can quote to support.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const apiError = error instanceof ApiError ? error : null;
  const forbidden = apiError?.status === 403;
  // `fetch` rejects with a TypeError when it never reached the host. Anything
  // else that got this far is a fault on our side, and saying "check your
  // connection" would send the reader looking in the wrong place.
  const offline = !apiError && error instanceof TypeError;

  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <span className="flex size-12 items-center justify-center rounded-full bg-danger-soft text-danger">
        {forbidden ? <ShieldAlert className="size-5" aria-hidden /> : <AlertCircle className="size-5" aria-hidden />}
      </span>
      <p className="text-sm font-semibold text-ink">
        {apiError?.message ?? t(offline ? 'errors.networkTitle' : 'errors.unexpectedTitle')}
      </p>
      {!apiError && (
        <p className="text-xs text-ink-muted">
          {t(offline ? 'errors.networkBody' : 'errors.unexpectedBody')}
        </p>
      )}
      {apiError?.requestId && (
        <p className="font-mono text-[11px] text-ink-subtle">
          {t('errors.requestId')}: {apiError.requestId}
        </p>
      )}
      {onRetry && !forbidden && (
        <Button variant="outline" size="sm" onClick={onRetry}>{t('common.retry')}</Button>
      )}
    </div>
  );
}
